import { useSearch as useBrowserSearch, navigate } from "wouter/use-browser-location";
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { fetchJson } from "@/lib/queryClient";
import { readCompanyScopesSnapshot, readCompanySnapshot, saveCompanyScopesSnapshot, saveCompanySnapshot } from "@/lib/companySnapshot";

/**
 * WHICH company Company Settings is showing, and how to ask the server for it.
 *
 * A company admin sees their own company; the sidebar passes `?projectId=`
 * from inside a project and the server resolves (and checks) the company from
 * it. A SaleFish Admin sees SaleFish by default and can switch to any company
 * assigned to the active project (Andrew, 2026-09-30) — the choice is
 * `?companyId=` in the URL, so every section, the rail's head, the card form
 * and the invoices follow it, and a reload keeps it. The server honours
 * `companyId` for a SUPER_ADMIN only.
 *
 * The RAW browser search, not the app's: inside the layered modal the app
 * router answers with the page underneath (lib/platformLayer.ts).
 */
export interface CompanyScope {
  isSuperAdmin: boolean;
  /** The active project the reader came from, when they came from one. */
  projectId?: string;
  /** A SaleFish Admin's chosen company; undefined = SaleFish. */
  companyId?: string;
  /** Appended to every /api/company-admin and /api/billing/company call. */
  apiSuffix: string;
  /** One cache entry per company shown. */
  scopeKey: string;
}

export function useCompanyScope(): CompanyScope {
  const { user } = useAuth();
  return useScopeFor(user);
}

/**
 * The same scope for a component that may render outside the AuthProvider —
 * the page header, which the route fallbacks and their tests draw on their
 * own. With no provider the reader is treated as not a SaleFish Admin, which
 * asks the server for the reader's own company: the safe default, since the
 * server honours `companyId` for a SUPER_ADMIN only anyway.
 *
 * useAuth reads its context unconditionally before it throws, so catching the
 * throw keeps the hook order stable.
 */
export function useOptionalCompanyScope(): CompanyScope {
  let user: ReturnType<typeof useAuth>["user"] | null = null;
  try {
    user = useAuth().user;
  } catch {
    user = null;
  }
  return useScopeFor(user);
}

function useScopeFor(user: { role?: string | null } | null | undefined): CompanyScope {
  // `ssrSearch` only answers where there is no `location` (a server render —
  // the fallback tests); in the browser this is location.search as before.
  const search = useBrowserSearch({ ssrSearch: "" });
  const params = new URLSearchParams(search);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const projectId = params.get("projectId") || undefined;
  const companyId = isSuperAdmin ? params.get("companyId") || undefined : undefined;
  const apiSuffix = isSuperAdmin
    ? companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""
    : projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  const scopeKey = isSuperAdmin ? companyId ?? "salefish" : projectId ?? "own";
  return { isSuperAdmin, projectId, companyId, apiSuffix, scopeKey };
}

/** The company being shown — one query shared by the page, the modal's head and the sidebar logo. */
export function useScopedCompany(scope: CompanyScope, enabled = true) {
  const query = useQuery<any>({
    queryKey: ["/api/company-admin/company", scope.scopeKey],
    queryFn: ({ signal }) => fetchJson<any>(`/api/company-admin/company${scope.apiSuffix}`, { signal }),
    enabled,
  });
  // Kept on the device for the next cold load to DRAW (lib/companySnapshot.ts) — never fed back into this cache.
  useEffect(() => {
    if (query.data) saveCompanySnapshot(scope.scopeKey, query.data);
  }, [query.data, scope.scopeKey]);
  return query;
}

/**
 * The company as this reader last saw it, while the live one is still out —
 * the device's copy (lib/companySnapshot.ts), for the header, the modal's rail
 * head and the route fallback to draw. Null once `pending` is false, and
 * never written to the query cache.
 */
export function useDeviceCompany(scope: CompanyScope, pending: boolean) {
  return useMemo(() => (pending ? readCompanySnapshot(scope.scopeKey) : null), [pending, scope.scopeKey]);
}

export interface ScopeOption {
  id: string;
  companyName: string;
  logoUrl?: string | null;
  isSaleFish: boolean;
  /** A SaleFish Partner: the company's whole plan is complimentary. */
  partner?: boolean;
}

/** SaleFish, then the active project's companies — what the switcher offers. */
export function useCompanyScopeOptions(scope: CompanyScope) {
  const q = scope.projectId ? `?projectId=${encodeURIComponent(scope.projectId)}` : "";
  const query = useQuery<ScopeOption[]>({
    queryKey: ["/api/company-admin/scopes", scope.projectId ?? null],
    queryFn: ({ signal }) => fetchJson<ScopeOption[]>(`/api/company-admin/scopes${q}`, { signal }),
    enabled: scope.isSuperAdmin,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (query.data) saveCompanyScopesSnapshot(scope.projectId, query.data);
  }, [query.data, scope.projectId]);
  return query;
}

/** The switcher's choices as this reader last saw them, while the live list is out (drawn, never cached). */
export function useDeviceCompanyScopeOptions(scope: CompanyScope, pending: boolean) {
  return useMemo(() => (pending && scope.isSuperAdmin ? readCompanyScopesSnapshot(scope.projectId) : null), [pending, scope.isSuperAdmin, scope.projectId]);
}

/** Switch to a company (undefined = SaleFish), keeping the path and every other parameter. */
export function switchCompanyScope(companyId: string | undefined) {
  const params = new URLSearchParams(window.location.search);
  if (companyId) params.set("companyId", companyId);
  else params.delete("companyId");
  const qs = params.toString();
  navigate(`${window.location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
}
